import React from "react";
import styled from "styled-components";
import {
  ModuleEnum,
  SearchResultAvatarDto,
  SearchResultModuleGroupDto,
} from "./types";
import Loader, { LoaderSize } from "../../../../global/loader/Loader";
import EmptyState from "../../../../global/common/EmptyState";
import {
  faChartSimple,
  faFileAlt,
  faSearch,
} from "@fortawesome/pro-solid-svg-icons";
import EmployeeAvatarNew from "../../../../global/avatars/EmployeeAvatarNew";
import {
  TextVariant,
  TypographyNew,
} from "../../../../global/typography/typography";
import { useTranslation } from "react-i18next";
import { baseUrl } from "../../../../config/globalVariables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type SearchResultsProps = {
  hasError?: boolean;
  searchTerm: string;
  items?: SearchResultModuleGroupDto[];
  loading: boolean;
  onResultItemClick?(avatar: SearchResultAvatarDto, module: ModuleEnum): void;
};

const SearchResultsContainer = styled.div<{ isActive: boolean }>`
  border-radius: ${(props) => props.theme.borderRadius.small};
  border: 1px solid ${(props) => props.theme.colors.grays4};
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0 2px 4px #4c4c4c24;
  margin-top: 10px;
  padding: 5px 0;
  max-height: 400px;
  overflow-y: scroll;
  min-height: ${({ isActive }) => (isActive ? "100px" : "auto")};
  position: relative;
  display: ${({ isActive }) => (isActive ? "flex" : "block")};
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`;

const ItemsHeader = styled.span`
  color: ${(props) => props.theme.colors.grays2};
  line-height: 16px;
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.normal};
  display: block;
  padding: 15px;
`;

const ItemList = styled.ul`
  margin: 0;
  padding: 0;
`;

const ItemListEntity = styled.li`
  margin: 0;
  list-style-type: none;
  padding: 15px;
  color: ${(props) => props.theme.colors.primary1};
  display: flex;
  flex-direction: row;

  &:hover {
    background-color: ${(props) => props.theme.colors.misc3};
    cursor: pointer;
  }
`;

const SearchResultsItemWrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.grays4};

  &:last-child {
    border: none;
  }
`;

const NamePositionContainer = styled.div`
  margin-left: 10px;
`;

type SearchResultsItemProps = {
  searchTerm: string;
  item: SearchResultModuleGroupDto;
  onClick?(avatar: SearchResultAvatarDto, module: ModuleEnum): void;
};

const HighlightedLabel = styled.span`
  background-color: ${(props) => props.theme.colors.misc2};
`;

const ErrorMessage = styled.span`
  color: ${(props) => props.theme.colors.accent1};
`;

type HighlightedTextProps = {
  text: string;
  term: string;
};

const HighlightedText: React.FC<HighlightedTextProps> = ({ text, term }) => {
  const parts = text.split(new RegExp(`(${term})`, "gi"));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <HighlightedLabel key={part}>{part}</HighlightedLabel>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

function getModuleName(m: ModuleEnum) {
  const { t } = useTranslation("searchbar");

  switch (m) {
    case ModuleEnum.DPF:
      return t("dpf");
    case ModuleEnum.ABSENCE:
      return t("absences");
    case ModuleEnum.PAYROLL:
      return t("payroll");
    case ModuleEnum.TIMETRACKING:
      return t("timetracking");
    case ModuleEnum.DOCUMENTS:
      return t("documents");
    case ModuleEnum.REPORTS:
      return t("reports");
  }
}

const mapSearchResultType = (
  module: ModuleEnum,
  dataItem: SearchResultAvatarDto,
  searchTerm: string
): React.ReactElement => {
  if (module === ModuleEnum.DPF) {
    return (
      <>
        <EmployeeAvatarNew
          imgUrl={
            dataItem.imgLink
              ? `${baseUrl}/file/account/${dataItem.imgLink}`
              : undefined
          }
          size={"SMALL"}
        />
        <NamePositionContainer>
          <HighlightedText
            text={`${dataItem.firstName} ${dataItem.lastName}`}
            term={searchTerm}
          />
          <TypographyNew variant={TextVariant.caption} color={"grey"}>
            {dataItem.position}
          </TypographyNew>
          {dataItem.title ? (
            <TypographyNew variant={TextVariant.subtitle2}>
              {dataItem.title}
            </TypographyNew>
          ) : null}
        </NamePositionContainer>
      </>
    );
  } else if (module === ModuleEnum.DOCUMENTS) {
    return (
      <>
        <FontAwesomeIcon icon={faFileAlt} size={"lg"} color={"black"} />
        <NamePositionContainer>
          <HighlightedText text={dataItem.title || ""} term={searchTerm} />
          <TypographyNew
            variant={TextVariant.caption}
            color={"grey"}
            inline={true}
            marginLeft={"10px"}
          >
            ({`${dataItem.firstName} ${dataItem.lastName}`})
          </TypographyNew>
        </NamePositionContainer>
      </>
    );
  } else if (module === ModuleEnum.REPORTS) {
    return (
      <>
        <FontAwesomeIcon icon={faChartSimple} size={"lg"} color={"black"} />
        <NamePositionContainer>
          <HighlightedText text={dataItem.title || ""} term={searchTerm} />
        </NamePositionContainer>
      </>
    );
  }

  return <></>;
};

const SearchResultsItem: React.FC<SearchResultsItemProps> = ({
  item,
  onClick,
  searchTerm,
}) => {
  return (
    <SearchResultsItemWrapper>
      <ItemsHeader>{getModuleName(item.module)}</ItemsHeader>
      <ItemList>
        {item.data.map((dataItem) => (
          <ItemListEntity
            onClick={() => onClick?.(dataItem, item.module)}
            key={dataItem.uuid}
          >
            {mapSearchResultType(item.module, dataItem, searchTerm)}
          </ItemListEntity>
        ))}
      </ItemList>
    </SearchResultsItemWrapper>
  );
};

const SearchResults: React.FC<SearchResultsProps> = ({
  items,
  loading,
  onResultItemClick,
  searchTerm,
  hasError = false,
}) => {
  if (!loading && !items && !hasError) {
    return null;
  }
  const hasNoResults = !loading && items?.length === 0;
  const isActiveContainer = loading || hasNoResults || hasError;

  return (
    <SearchResultsContainer isActive={isActiveContainer}>
      {loading && <Loader size={LoaderSize.small} />}
      {!loading &&
        items?.map((item) => (
          <SearchResultsItem
            searchTerm={searchTerm}
            onClick={onResultItemClick}
            item={item}
            key={item.module}
          />
        ))}
      {hasNoResults && <EmptyState icon={faSearch} message={"No results"} />}
      {hasError && (
        <ErrorMessage>{`Suche fehlgeschlagen, versuchen Sie es später erneut.`}</ErrorMessage>
      )}
    </SearchResultsContainer>
  );
};

export default SearchResults;
